import { dayjs } from "components/ui/DatePicker/date.utils";
import { KPIColor } from "components/ui/KPI/kpi.consts";
import { KPITimeScale } from "components/ui/KPI/kpi.types";

const DATE_FORMAT = "YYYY-MM-DD";

type FormatKPIValueOptions = {
    numberPrecision?: number;
    suffix?: string;
};
export const formatKPIValue = (value: unknown, options?: FormatKPIValueOptions): string => {
    if (value === null || value === undefined) return "No Data";

    let formattedValue: string;
    if (typeof value === "number") {
        const { format: formatNumber } = new Intl.NumberFormat("en-US", {
            maximumFractionDigits: options?.numberPrecision ?? 2,
        });
        formattedValue = formatNumber(value);
    } else {
        formattedValue = value.toString();
    }

    if (options?.suffix) {
        formattedValue += `${options.suffix}`;
    }

    return formattedValue;
};

export const getLastValueTitle = (dateFrom: unknown, dateTo: unknown) => {
    if (!dateFrom || !dateTo) {
        console.error("Missing dateFrom or dateTo");
        return "";
    }

    const numOfSelectedDays = dayjs(dateTo as string).diff(dateFrom as string, "day");

    if (numOfSelectedDays > 90) {
        return "Last Selected Full Month";
    }

    return "Last Selected Full Week";
};

export const resolveKPIColor = (colorOption?: KPIColor): string => {
    return KPIColor[colorOption ?? "blue"];
};

export const getKPITimeScale = (fromDate: string | number, toDate: string | number): KPITimeScale => {
    const from = dayjs(fromDate);
    const to = dayjs(toDate);

    return to.diff(from, "days") > 90 ? "month" : "week";
};

export const toKPITextData = (data: any[], options?: { valueKey?: string }): number | null => {
    if (!data?.length) return null;

    const valueKey = options?.valueKey ?? "TextValue";
    if (!(valueKey in data.at(0)!)) return null;

    return data.at(0)[valueKey];
};

type DateRange = {
    from: string;
    to: string;
};

/**
 * Calculates the correct date range limits for KPI widget.
 *
 * Adjusts the `to` date so that it aligns with the end of the previous
 * complete time period (e.g., week, month, etc.) and limits it to not go beyond
 * the last complete period from today. Sets the `from` date 5 time units prior to the new `to` date.
 *
 * A period ending on the current day (today) is not considered complete.
 *
 */
export const getDateRangeLimits = (dateRange: DateRange, timeScale: KPITimeScale): DateRange => {
    let from = dayjs(dateRange.from);
    let to = dayjs(dateRange.to);

    // If dateTo is in an uncomplete week/month, move it to the end of the previous complete one
    const dateToIsEndOfUnit = to.isSame(to.endOf(timeScale), "day");
    if (!dateToIsEndOfUnit) {
        to = to.startOf(timeScale).subtract(1, "day");
    }

    const lastEndUnitFromToday = dayjs().startOf(timeScale).subtract(1, "day");

    if (to.isAfter(lastEndUnitFromToday, "day")) {
        to = lastEndUnitFromToday;
    }

    from = to.subtract(5, timeScale).startOf(timeScale);

    return { from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) };
};

type KPIDataPoint = { date: string; value: number };

type FormatKPIDataOptions<T> = {
    getValue: (item: T) => number;
    getDate: (item: T) => string;
};

export const formatKPIData = <T>(data: T[], options: FormatKPIDataOptions<T>): KPIDataPoint[] => {
    const { getValue, getDate } = options;

    return data.map((item) => ({
        date: dayjs(getDate(item)).format(DATE_FORMAT),
        value: getValue(item),
    }));
};

/**
 * Generates the complete KPI dataset within a given date range.
 *
 * Fills in missing KPI data points with zero values to ensure a continuous dataset
 * between the `from` and `to` dates.
 *
 */
export const getKPIFullDataset = (dateRange: DateRange, dataPoints: KPIDataPoint[]): KPIDataPoint[] => {
    const from = dayjs(dateRange.from);
    const to = dayjs(dateRange.to);

    const dataMap = new Map(dataPoints.map((item) => [item.date, item.value]));

    const result: KPIDataPoint[] = [];

    let current = from;

    while (current.isBefore(to, "day") || current.isSame(to, "day")) {
        const dateAsString = current.format(DATE_FORMAT);

        result.push({
            date: dateAsString,
            value: dataMap.get(dateAsString) ?? null,
        });

        current = current.add(1, "day");
    }

    return result;
};

/**
 * Aggregates an array of data points based on a specified time scale, calculating the average value for each period.
 * The null values are ignored in the average calculation.
 * If a period contains only null values, the average for that period falls back to 0.
 *
 */
export const getUnitAggregates = (data: KPIDataPoint[], timeScale: KPITimeScale): KPIDataPoint[] => {
    const sortedData = data.toSorted((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const groupedByEndOfUnit = Object.groupBy(sortedData, (record) => {
        const lastDayOfUnit = dayjs(record.date).endOf(timeScale);

        return lastDayOfUnit.format(DATE_FORMAT);
    });

    const unitAggregates = Object.entries(groupedByEndOfUnit).map(([lastDayOfUnit, records]) => {
        const validRecords = records.filter((r) => r.value != null);
        const sumRecordValue = validRecords.reduce((sum, record) => sum + record.value, 0);

        const averageRecordValue = validRecords.length ? sumRecordValue / validRecords.length : 0;

        return {
            date: lastDayOfUnit,
            value: averageRecordValue,
        };
    });

    return unitAggregates;
};
